import { Button, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined'
import SendIcon from '@mui/icons-material/Send'
import { Feature, TwinContextSource, TwinConversation, TwinMessage } from 'api/model'
import { useActiveOrganization } from 'hooks/use-organization'
import { twinsDeleteConversation } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { authenticatedUserAtom, toastAtom } from 'state'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import VerboseLogModal from 'components/Debug'
import { useFetchInternalConversation } from 'hooks/use-conversation'
import { useFetchConversationMessages } from 'hooks/use-messages'
import {
  CHECK_CALENDAR_SUGGESTION,
  IMPROVE_TWIN_PERFORMANCE_SUGGESTION,
  SUMMARIZE_EMAIL_SUGGESTION,
  defaultSuggestedQuestions,
} from 'hooks/use-instructions'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import SetupPermissionsModal from 'components/Permissions/SetupPermissionsModal'
import { featurePermissionsAtomFamily } from 'state/permission'
import { streamedTwinsAsk } from 'components/Conversations'
import { format } from 'date-fns'
import { myTwinAtom } from 'state/my-twin'
import ProfilePhoto from 'components/ProfilePhoto'
import styled from '@emotion/styled'
import { COLORS } from 'app-constants'
import TextWithMarkdown from 'components/TextWithMarkdown/TextWithMarkdown'

export const TwinChatBubbleStack = styled(Stack)`
  border-radius: 20px;
  position: relative;
  margin-left: 32px;
`

const Header = ({
  internalConversationId,
  setInternalConversation,
  setVerboseOutput,
  setContextSources,
  onRefreshClicked,
}: {
  internalConversationId: string
  setInternalConversation: React.Dispatch<React.SetStateAction<TwinConversation | null>>
  setVerboseOutput: React.Dispatch<React.SetStateAction<string | null>>
  setContextSources: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  onRefreshClicked: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const myTwin = useRecoilValue(myTwinAtom)

  const onRefreshClick = async () => {
    if (!activeOrganization || !internalConversationId) {
      onRefreshClicked()
      // clear
      return
    }

    try {
      // we should delete the conversation with the twin - then clear out the conversationMessages
      await twinsDeleteConversation(activeOrganization, internalConversationId)
      setInternalConversation(null)
      // should also clear the debug output
      setVerboseOutput('')
      setContextSources([])
      setToast({
        severity: 'success',
        title: 'Successfully refreshed conversation',
      })
      onRefreshClicked()
    } catch (error) {
      setToast({
        severity: 'error',
        title: 'Failed to refresh conversation',
      })
    }
  }

  // TODO: add error state if there is no twin
  if (!myTwin) {
    return null
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
      p={2}
      sx={{
        borderTopRightRadius: '12px',
        borderTopLeftRadius: '12px',
        backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
      }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ProfilePhoto
          hasOutline={true}
          src={myTwin.picture || myTwin.user.picture}
          size="32px"
          name={myTwin.name || myTwin.user.name || myTwin.user.email}
        />

        <Typography
          variant="h6"
          sx={{
            color: COLORS.PURE_WHITE,
          }}>
          {myTwin?.name ?? 'Chat with your twin'}
        </Typography>
      </Stack>

      <IconButton onClick={onRefreshClick} size="small">
        <RefreshOutlinedIcon
          sx={{
            color: COLORS.PURE_WHITE,
          }}
        />
      </IconButton>
    </Stack>
  )
}

const TwinMessageBubble = ({ text, isIntro }: { text: string; isIntro: boolean }) => {
  return (
    <Stack
      borderRadius="16px"
      px="12px"
      py="8px"
      alignItems="center"
      sx={{
        width: 'fit-content',
        backgroundColor: COLORS.GRAY_8,
        borderBottomLeftRadius: '0px',
        // make the bubble slide in from the left using animation
        animation: isIntro ? 'slideinleft 0.5s' : 'none',
        '@keyframes slideinleft': {
          from: { transform: 'translateX(-100%)' },
          to: { transform: 'translateX(0)' },
        },
      }}>
      <TextWithMarkdown text={text} />
    </Stack>
  )
}

const UserMessageBubble = ({
  isSuggestion,
  text,
  onSuggestionClick,
}: {
  isSuggestion?: boolean
  text: string
  onSuggestionClick?: (text: string) => void
}) => {
  return (
    <Stack
      direction="row"
      borderRadius="16px"
      px="12px"
      py="8px"
      alignItems="center"
      onClick={() => onSuggestionClick && onSuggestionClick(text)}
      sx={{
        backgroundColor: COLORS.SPECIAL_FILL,
        width: 'fit-content',
        alignSelf: 'flex-end',
        cursor: isSuggestion ? 'pointer' : 'default',
        // make the animation slide in from the right
        animation: isSuggestion ? 'slideinright 0.5s' : 'none',
        '@keyframes slideinright': {
          from: { transform: 'translateX(100%)' },
          to: { transform: 'translateX(0)' },
        },
      }}>
      {isSuggestion && (
        <CallMadeOutlinedIcon
          sx={{
            height: '16px',
            width: '16px',
            color: COLORS.GRAY_0,
            marginRight: '8px',
          }}
        />
      )}
      <TextWithMarkdown text={text} />
    </Stack>
  )
}

// responsible for rendering the list of messages
const Messages = ({
  conversationMessages,
  isLoadingChatbot,
  onSuggestionClick,
  verboseOutput,
  streamedMessage,
  pendingTwinMessage,
  suggestedQuestions,
  pendingUserQuestion,
}: {
  conversationMessages: TwinMessage[]
  isLoadingChatbot: boolean
  onSuggestionClick?: (text: string) => void
  verboseOutput: string | null
  streamedMessage: string // the message that currently being streamed that represents the twin's response
  pendingTwinMessage: TwinMessage | null // the twin message that is currently being streamed
  suggestedQuestions: string[]
  pendingUserQuestion: string | null
}) => {
  const isMobile = useCheckMobileScreen()

  const conversationMessagesRef = useRef(conversationMessages)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // add a use effect for scrolling to the bottom whenever streamed message changes
  useEffect(() => {
    // debounce scroll by 100ms
    const timeout = setTimeout(() => {
      setImmediate(() => {
        window.requestAnimationFrame(() =>
          messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
        )
      })
    }, 10)

    return () => {
      clearTimeout(timeout)
    }
  }, [streamedMessage, pendingUserQuestion])

  useEffect(() => {
    const shouldScrollToEnd = conversationMessagesRef.current?.length < conversationMessages.length

    if (shouldScrollToEnd) {
      setImmediate(() => {
        window.requestAnimationFrame(() =>
          messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
        )
      })
    }
  }, [conversationMessages, conversationMessagesRef])

  // TODO: add loading state
  if (isLoadingChatbot) {
    return null
  }

  return (
    <Stack
      px={2}
      spacing={1}
      sx={{
        overflowY: 'scroll',
        height: verboseOutput ? 'calc(100% - 132px)' : 'calc(100% - 102px)',
      }}>
      <TwinMessageBubble text={'Hi! How can I help you today?'} isIntro />
      {!pendingTwinMessage &&
        suggestedQuestions.map((sq, idx) => {
          return <UserMessageBubble key={idx} isSuggestion={true} text={sq} onSuggestionClick={onSuggestionClick} />
        })}
      {conversationMessages?.map((m: TwinMessage) => {
        return (
          <Stack key={m.id} spacing={1}>
            <Typography variant="h7" textAlign={'center'} color="#666666">
              {format(new Date(m.date_created), 'MMM dd, yyyy')} at {format(new Date(m.date_created), 'hh:mm a')}
            </Typography>
            <UserMessageBubble isSuggestion={false} text={m.question} />
            <TwinMessageBubble text={m.twin_answer ?? ''} isIntro={false} />
          </Stack>
        )
      })}

      {pendingTwinMessage?.date_created && (
        <Typography variant="h7" textAlign={'center'} color="#666666">
          {format(new Date(pendingTwinMessage.date_created), 'MMM dd, yyyy')} at{' '}
          {format(new Date(pendingTwinMessage.date_created), 'hh:mm a')}
        </Typography>
      )}

      {pendingTwinMessage && ( // if there is a streamed message, we should also show the user's question
        <Stack
          alignSelf="flex-end"
          width="fit-content"
          borderRadius="16px"
          px="12px"
          py="8px"
          alignItems="center"
          mb={2}
          sx={{
            backgroundColor: COLORS.SPECIAL_FILL,
          }}>
          <Typography variant="pS" textAlign="right">
            {pendingTwinMessage.question}
          </Typography>
        </Stack>
      )}

      {streamedMessage && (
        <TwinChatBubbleStack
          sx={{
            width: isMobile ? 'auto' : 'fit-content',
            backgroundColor: COLORS.GRAY_8,
            px: '12px',
            py: '8px',
          }}>
          <TextWithMarkdown text={streamedMessage} />
        </TwinChatBubbleStack>
      )}
      {pendingUserQuestion && (
        <Typography variant="h7" textAlign={'center'} color="#666666">
          {format(new Date(), 'MMM dd, yyyy')} at {format(new Date(), 'hh:mm a')}
        </Typography>
      )}
      {pendingUserQuestion && <UserMessageBubble isSuggestion={false} text={pendingUserQuestion} />}
      <Stack ref={messagesEndRef} />
    </Stack>
  )
}

const Chatbot = ({
  internalConversation,
  setInternalConversation,
  conversationMessages,
  setConversationMessages,
  isLoadingChatbot,
  setIsCreatingNewConversation,
}: {
  internalConversation: TwinConversation | null
  setInternalConversation: React.Dispatch<React.SetStateAction<TwinConversation | null>>
  conversationMessages: TwinMessage[]
  setConversationMessages: React.Dispatch<React.SetStateAction<TwinMessage[]>>
  isLoadingChatbot: boolean
  setIsCreatingNewConversation: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const user = useRecoilValue(authenticatedUserAtom)
  const myTwin = useRecoilValue(myTwinAtom)

  const [inputText, setInputText] = useState<string>('')
  const onInputTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value)
  }

  // used for debugging purposes
  const [verboseOutput, setVerboseOutput] = useState<string | null>(null)
  const [contextSources, setContextSources] = useState<TwinContextSource[]>([])
  const [isVerboseLogModalOpen, setIsVerboseLogModalOpen] = useState<boolean>(false)

  // permissions modal state
  const [feature, setFeature] = useState<Feature | null>(null)
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)

  const gmailPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.gmail))
  const googleCalendarPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.google_calendar))
  const googleDrivePermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.google_drive))

  // used when we are getting a stream response back for a question that was asked
  const [streamedMessage, setStreamedMessage] = useState<string>('')
  const [pendingTwinMessage, setPendingTwinMessage] = useState<TwinMessage | null>(null)
  const [tempTwinMessage, setTempTwinMessage] = useState<TwinMessage | null>(null)

  // used to keep track of the user's latest explicitly entered question
  const [pendingUserQuestion, setPendingUserQuestion] = useState<string | null>(null)

  useEffect(() => {
    if (pendingTwinMessage && pendingUserQuestion) {
      setPendingUserQuestion(null)
    }
  }, [pendingTwinMessage, pendingUserQuestion])

  const hydrateConversationMessages = useCallback(
    (twinMessage: TwinMessage) => {
      // we need to update conversationMessages in recoil
      setConversationMessages((prev) => {
        return [...prev, twinMessage]
      })
    },
    [setConversationMessages]
  )

  useEffect(() => {
    // we need to update conversation messages for the new conversation
    if (internalConversation && tempTwinMessage && !conversationMessages.find((c) => c.id === tempTwinMessage.id)) {
      hydrateConversationMessages(tempTwinMessage)
      setTempTwinMessage(null)
    }
  }, [tempTwinMessage, internalConversation, hydrateConversationMessages, conversationMessages])

  // we store a new twin conversation in local state so that we can update the recent conversations and conversations grouped by twin once we have finished the while loop for the streaming call
  const [tempTwinConversation, setTempTwinConversation] = useState<TwinConversation | null>(null)
  useEffect(() => {
    if (tempTwinConversation && myTwin) {
      setInternalConversation(tempTwinConversation)
      setTempTwinConversation(null)
    }
  }, [tempTwinConversation, internalConversation, myTwin, setInternalConversation])

  // temp local state used to update the verbose output once we're done streaming
  const [tempVerboseOutput, setTempVerboseOutput] = useState<string>('')
  useEffect(() => {
    if (tempVerboseOutput) {
      setVerboseOutput(tempVerboseOutput)
      setTempVerboseOutput('')
    }
  }, [tempVerboseOutput, setVerboseOutput])

  const [tempContextSources, setTempContextSources] = useState<TwinContextSource[]>([])
  useEffect(() => {
    if (tempContextSources.length > 0) {
      setContextSources(tempContextSources)
      setTempContextSources([])
    }
  }, [tempContextSources, setContextSources])

  const onSubmit = async (suggestedQuestion?: string) => {
    if ((!inputText && !suggestedQuestion) || !activeOrganization) {
      return
    }

    try {
      setIsSubmitting(true)

      if (!internalConversation) {
        setIsCreatingNewConversation?.(true)
      }

      // clear input
      setInputText('')
      // clear suggested questions
      if (inputText) {
        setSuggestedQuestions([])
      }

      // set the pending user question
      if (suggestedQuestion) {
        setPendingUserQuestion(suggestedQuestion)
      } else if (inputText) {
        setPendingUserQuestion(inputText)
      }

      await streamedTwinsAsk(
        activeOrganization,
        myTwin?.id ?? '',
        suggestedQuestion || inputText,
        user?.is_system_admin ?? false,
        setPendingTwinMessage,
        setTempTwinMessage,
        setStreamedMessage,
        setTempTwinConversation,
        true,
        {
          conversation: internalConversation ?? undefined,
          setTempContextSources,
          setTempVerboseOutput,
        }
      )
    } catch (error) {
      setToast({
        severity: 'error',
        title: 'Failed to send message',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const getAcceptedPermissions = () => {
    switch (feature) {
      case Feature.gmail:
        return gmailPermissions?.acceptedPermissions ?? []
      case Feature.google_calendar:
        return googleCalendarPermissions?.acceptedPermissions ?? []
      case Feature.google_drive:
        return googleDrivePermissions?.acceptedPermissions ?? []
      default:
        return []
    }
  }

  const getMissingPermissions = useCallback(() => {
    switch (feature) {
      case Feature.gmail:
        return gmailPermissions?.missingPermissions ?? []
      case Feature.google_calendar:
        return googleCalendarPermissions?.missingPermissions ?? []
      case Feature.google_drive:
        return googleDrivePermissions?.missingPermissions ?? []
      default:
        return []
    }
  }, [feature, gmailPermissions, googleCalendarPermissions, googleDrivePermissions])

  const missingPermissions = useMemo(() => {
    return getMissingPermissions()
  }, [getMissingPermissions])

  useEffect(() => {
    // when feature is updated, we may want to show the permissions modal
    if (feature && missingPermissions.length > 0) {
      setIsPermissionsModalOpen(true)
    }
  }, [feature, missingPermissions])

  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
    setFeature(null)
  }

  const onSuggestionClick = (suggestionText: string) => {
    // depending on what the suggestion text is, we should show the appropriate permissions modal
    const isMissingPermissions =
      (gmailPermissions?.missingPermissions &&
        gmailPermissions?.missingPermissions.length > 0 &&
        suggestionText === SUMMARIZE_EMAIL_SUGGESTION) ||
      (googleCalendarPermissions?.missingPermissions &&
        googleCalendarPermissions?.missingPermissions.length > 0 &&
        suggestionText === CHECK_CALENDAR_SUGGESTION) ||
      (googleDrivePermissions?.missingPermissions &&
        googleDrivePermissions.missingPermissions.length > 0 &&
        suggestionText === IMPROVE_TWIN_PERFORMANCE_SUGGESTION)

    if (suggestionText === IMPROVE_TWIN_PERFORMANCE_SUGGESTION) {
      setConversationMessages((prev) => [
        ...prev,
        {
          id: 'temp',
          twin_id: myTwin?.id ?? '',
          conversation_id: internalConversation?.id ?? '',
          question: IMPROVE_TWIN_PERFORMANCE_SUGGESTION,
          save_useranswer: false,
          date_created: new Date().toISOString(),
          date_updated: new Date().toISOString(),
          twin_answer:
            'In order for your digital twin to surface useful insights and generate accurate responses to incoming messages, you can add relevant data to provide context for the twin to use—the more data you provide, the more effective your twin will be. \n\n You can add notes, emails, calendars, files, and instructions to your digital twin’s knowledge base by navigating to http://app.jelled.ai/my-twin \n\n Learn more about the data types at http://jelled.ai/features/knowledge-management',
        },
      ])
      setSuggestedQuestions([])
      return
    }

    if (isMissingPermissions && suggestionText) {
      switch (suggestionText) {
        case SUMMARIZE_EMAIL_SUGGESTION:
          setFeature(Feature.gmail)
          break
        case CHECK_CALENDAR_SUGGESTION:
          setFeature(Feature.google_calendar)
          break
      }
    } else {
      // update UI to only show the user's question
      setPendingUserQuestion(suggestionText)
      setSuggestedQuestions([])
      onSubmit(suggestionText)
    }
  }

  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(
    conversationMessages.length === 0 ? defaultSuggestedQuestions : []
  )

  return (
    <Stack
      position="relative"
      height="70vh"
      width={isMobile ? '100%' : '395px'}
      border={`1px solid ${COLORS.GRAY_5}`}
      sx={{
        backgroundColor: COLORS.PURE_WHITE,
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        borderBottomLeftRadius: '16px',
        borderBottomRightRadius: '16px',
      }}>
      <Header
        internalConversationId={internalConversation?.id ?? ''}
        setInternalConversation={setInternalConversation}
        setVerboseOutput={setVerboseOutput}
        setContextSources={setContextSources}
        onRefreshClicked={() => {
          setSuggestedQuestions(defaultSuggestedQuestions)
          // should also clear the hardcoded message
          setConversationMessages((prev) => prev.filter((m) => m.question !== IMPROVE_TWIN_PERFORMANCE_SUGGESTION))
        }}
      />

      <Messages
        isLoadingChatbot={isLoadingChatbot}
        conversationMessages={conversationMessages}
        onSuggestionClick={onSuggestionClick}
        verboseOutput={verboseOutput}
        streamedMessage={streamedMessage}
        pendingTwinMessage={pendingTwinMessage}
        suggestedQuestions={suggestedQuestions}
        pendingUserQuestion={pendingUserQuestion}
      />

      <Stack width="100%" position="relative" px={2}>
        {isSubmitting && !streamedMessage && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            py={2}
            sx={{
              backgroundColor: COLORS.PURE_WHITE,
            }}>
            <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
            <Typography variant="h6">Your twin is generating a response...</Typography>
          </Stack>
        )}
        <TextField
          label=""
          placeholder="Ask away..."
          value={inputText}
          onChange={onInputTextChange}
          disabled={isSubmitting}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSubmit()
            }
          }}
          fullWidth
          sx={{
            my: 2,
          }}
          InputProps={{
            endAdornment: (
              <Button
                disabled={!inputText}
                onClick={() => {
                  onSubmit()
                }}>
                <SendIcon
                  style={{
                    height: '36px',
                    width: '36px',
                    padding: '6px',
                    borderRadius: '6px',
                    background: inputText ? COLORS.JELLED_PRIMARY_BLUE : 'transparent',
                    fill: inputText ? COLORS.PURE_WHITE : COLORS.GRAY_3,
                    transition: 'background 0.5s, fill 0.5s',
                  }}
                />
              </Button>
            ),
          }}
        />
        {verboseOutput && (
          <Stack>
            <Button
              onClick={() => setIsVerboseLogModalOpen?.(true)}
              sx={{
                width: 'fit-content',
                mb: 2,
              }}>
              Open verbose log
            </Button>
            {isVerboseLogModalOpen && (
              <VerboseLogModal
                contextSources={contextSources || []}
                onClose={() => setIsVerboseLogModalOpen?.(false)}
                verboseOutput={verboseOutput}
              />
            )}
          </Stack>
        )}
      </Stack>
      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          isFromChatbot={true}
          onClose={onClosePermissionsModal}
          acceptedPermissions={getAcceptedPermissions()}
          missingPermissions={missingPermissions}
          feature={feature}
        />
      )}
    </Stack>
  )
}

const ChatbotContainer = () => {
  const { internalConversation, setInternalConversation } = useFetchInternalConversation()
  // using the internal conversation id, we should fetch the list of messages

  const [isCreatingNewConversation, setIsCreatingNewConversation] = useState<boolean>(false)

  const {
    conversationMessages,
    setConversationMessages,
    isLoading: isLoadingChatbot,
  } = useFetchConversationMessages(internalConversation?.twin_id ?? '', internalConversation?.id ?? '', {
    isCreatingNewConversation,
  })

  // we need to fetch permissions for gmail, google calendar, and google drive
  useFetchCheckPermissions(Feature.gmail)
  useFetchCheckPermissions(Feature.google_calendar)
  useFetchCheckPermissions(Feature.google_drive)

  return (
    <Chatbot
      isLoadingChatbot={isLoadingChatbot}
      internalConversation={internalConversation}
      setInternalConversation={setInternalConversation}
      conversationMessages={conversationMessages}
      setConversationMessages={setConversationMessages}
      setIsCreatingNewConversation={setIsCreatingNewConversation}
    />
  )
}

export default ChatbotContainer
